import { AuthLayout } from "@/components/src/signin/signin-components/auth-layout.component";
import { zodResolver } from "@hookform/resolvers/zod";
import axios, { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { z } from "zod";
import { environment } from "../../lib/utils/environment";
import { Logger } from "@/lib/logger/Logger";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const InviteeSignUpSchema = z.object({
	email: z.string().email(),
	password: z.string().min(6),
	firstName: z.string(),
	lastName: z.string(),
});

type InviteeSingUpType = z.infer<typeof InviteeSignUpSchema>;

export const InviteeSignup = () => {
	const [search] = useSearchParams();
	const [loading, setLoading] = useState<boolean>(false);
	const navigate = useNavigate();

	const { register, handleSubmit } = useForm<InviteeSingUpType>({
		resolver: zodResolver(InviteeSignUpSchema as any),
		defaultValues: {
			email: search.get("email") ?? "",
		},
	});

	const onSubmit = async () => {
		await handleSubmit(async (data) => {
			try {
				setLoading(true);
				const res = await axios({
					method: "POST",
					url: `${environment.VITE_PDF_MICROSERVICE_URL}/register/lab-invite`,
					data: {
						first_name: data.firstName,
						last_name: data.lastName,
						email: data.email,
						password: data.password,
					},
				});

				if (res.status == 200) navigate("/");
			} catch (e: unknown) {
				Logger.error(e instanceof AxiosError ? e.message : e);
				return;
			} finally {
				setLoading(false);
			}
		})();
	};

	return (
		<>
			{loading && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white p-6 rounded-lg">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
					</div>
				</div>
			)}
			<AuthLayout title="Erstellen Sie Ihren Account" subtitle="">
				<form onSubmit={onSubmit} className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="email">E-Mail *</Label>
						<Input
							id="email"
							type="email"
							autoComplete="email"
							required
							className="h-12"
							aria-label="E-Mail Adresse"
							data-testid="invitee-signup-email-input"
							{...register("email")}
						/>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label htmlFor="firstName">Vorname *</Label>
							<Input
								id="firstName"
								autoComplete="given-name"
								required
								className="h-12"
								aria-label="Vorname"
								data-testid="invitee-signup-firstname-input"
								{...register("firstName")}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="lastName">Nachname *</Label>
							<Input
								id="lastName"
								autoComplete="family-name"
								required
								className="h-12"
								aria-label="Nachname"
								data-testid="invitee-signup-lastname-input"
								{...register("lastName")}
							/>
						</div>
					</div>
					<div className="space-y-2">
						<Label htmlFor="password">Passwort *</Label>
						<Input
							id="password"
							type="password"
							autoComplete="new-password"
							required
							className="h-12"
							aria-label="Passwort"
							data-testid="invitee-signup-password-input"
							{...register("password")}
						/>
					</div>
					<Button
						type="submit"
						disabled={loading}
						size="lg"
						className="w-full h-12 text-base mt-6"
						aria-label="Registrieren"
						data-testid="invitee-signup-submit-button"
					>
						{loading ? "Wird registriert..." : "Registrieren"}
					</Button>
				</form>

				<div className="mt-6 text-center">
					<Link
						to="/"
						className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
					>
						Bereits registriert? Zum Login
					</Link>
				</div>
			</AuthLayout>
		</>
	);
};
